import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV, LOGO } from "../../data/content";

export const Navbar = ({ onNavigate }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    onNavigate(id);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-[100] transition-colors duration-500 ${
          scrolled ? "bg-background/90 backdrop-blur-md border-b border-border" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="mx-auto max-w-[1600px] px-4 md:px-10 h-[68px] md:h-[84px] flex items-center justify-between">
          <button
            onClick={() => go("showreel")}
            className="flex items-center group"
            data-testid="logo-btn"
          >
            <img src={LOGO} alt="CF Action Design" className="h-[42px] md:h-[58px] w-auto object-contain transition-all" />
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => go(n.id)}
                className="font-mono-c text-[11px] tracking-[0.18em] uppercase text-muted-foreground hover:text-foreground transition-colors link-underline"
                data-testid={`nav-${n.id}`}
              >
                {n.label}
              </button>
            ))}
          </nav>

          <button
            onClick={() => go("contact")}
            className="hidden lg:inline-flex items-center border border-border px-5 py-2 font-mono-c text-[11px] tracking-[0.18em] uppercase hover:bg-foreground hover:text-background transition-colors"
            data-testid="nav-call-btn"
          >
            Enquire
          </button>

          <div className="lg:hidden flex items-center gap-3">
            <button
              onClick={() => go("contact")}
              className="border border-[hsl(var(--accent))] bg-[hsl(var(--accent))/10] px-3 py-1.5 font-mono-c text-[10px] tracking-[0.15em] uppercase text-[hsl(var(--accent))] active:bg-[hsl(var(--accent))] active:text-white transition-colors"
              data-testid="mobile-quick-enquire"
            >
              Enquire
            </button>
            <button
              className="text-foreground p-1"
              onClick={() => setOpen(true)}
              data-testid="menu-open-btn"
              aria-label="Open menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed inset-0 z-[110] bg-background/98 backdrop-blur-xl flex flex-col justify-between p-6 border-b border-border overflow-y-auto"
            data-testid="mobile-menu"
          >
            {/* Mobile Header Top */}
            <div className="flex items-center justify-between border-b border-border/80 pb-5">
              <div className="flex items-center gap-2">
                <img src={LOGO} alt="CF Action Design" className="h-9 w-auto object-contain" />
                <span className="font-mono-c text-[9px] tracking-[0.2em] uppercase text-[hsl(var(--accent))] border border-[hsl(var(--accent))/30] px-2 py-0.5 rounded-full">
                  Action Dept
                </span>
              </div>
              <button 
                onClick={() => setOpen(false)} 
                className="p-2 border border-border rounded-full hover:bg-white/10 active:scale-95 transition-all"
                data-testid="menu-close-btn" 
                aria-label="Close menu"
              >
                <X size={22} />
              </button>
            </div>

            {/* Mobile Nav Links */}
            <nav className="my-auto py-8 flex flex-col gap-2">
              {NAV.map((n, i) => (
                <motion.button
                  key={n.id}
                  initial={{ opacity: 0, x: -25 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  onClick={() => go(n.id)}
                  className="group flex items-center justify-between text-left py-3 border-b border-border/40 active:px-2 transition-all"
                  data-testid={`mnav-${n.id}`}
                >
                  <div className="flex items-center gap-4">
                    <span className="font-mono-c text-xs text-[hsl(var(--accent))]">0{i + 1}</span>
                    <span className="font-display uppercase text-3xl sm:text-4xl tracking-tight text-foreground/90 group-hover:text-[hsl(var(--accent))] transition-colors">
                      {n.label}
                    </span>
                  </div>
                  <span className="font-mono-c text-xs text-muted-foreground group-hover:text-[hsl(var(--accent))] transition-colors">
                    →
                  </span>
                </motion.button>
              ))}
            </nav>

            {/* Mobile Menu Footer Info */}
            <div className="pt-6 border-t border-border/80 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <span className="kicker">Bengaluru · India</span>
                <span className="font-mono-c text-[10px] text-[hsl(var(--accent))]">Chethan D&apos;Souza</span>
              </div>
              <button
                onClick={() => go("contact")}
                className="w-full bg-[hsl(var(--accent))] text-white py-3.5 font-mono-c text-xs tracking-[0.2em] uppercase active:bg-white active:text-black transition-colors"
              >
                Book Action Team ↗
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
